import React, {useEffect, useMemo, useState} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Rating,
    Stack,
    TextField,
    ThemeProvider,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {isIirProductType} from '../../products/productTypes';
import {isApprovedReportStatus} from '../../products/utils/reportStatusColor';
import UserAvatar from '../../../components/UserAvatar';
import theme from '../../../theme';
import useIirEvaluations from './useIirEvaluations';

/**
 * Evaluations are API-gated to approved IIRs; skip UI + fetch otherwise (avoids 403 on drafts).
 */
export function isIirEvaluationEligible({productType, status, enabled = true} = {}) {
    return Boolean(enabled)
        && isIirProductType(productType)
        && isApprovedReportStatus(status);
}

function formatReviewDate(value) {
    if (!value) return '';
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value);
        return d.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'});
    } catch {
        return String(value);
    }
}

function formatScore(value) {
    if (value == null || Number.isNaN(Number(value))) return '0.0';
    return Number(value).toFixed(1);
}

function ReviewerHeader({review, avatarSize = 28}) {
    const name = review.reviewerDisplayName || review.reviewerUserId || 'Reviewer';
    const when = formatReviewDate(review.updatedAt || review.createdAt);

    return (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <UserAvatar
                userId={review.reviewerUserId}
                user={{displayName: name, userId: review.reviewerUserId}}
                sx={{width: avatarSize, height: avatarSize, fontSize: '0.75rem'}}
            />
            <Typography variant="body2" sx={{fontWeight: 600}}>
                {name}
            </Typography>
            {when && (
                <Typography variant="caption" color="text.secondary">
                    {when}
                </Typography>
            )}
        </Stack>
    );
}

function AccordionTitle({averageScore, count}) {
    const hasReviews = count > 0 && averageScore != null;
    const displayAvg = hasReviews ? formatScore(averageScore) : null;

    return (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{py: 0.25}}>
            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                Analyst Reviews
            </Typography>
            {hasReviews ? (
                <>
                    <Rating
                        value={Number(averageScore)}
                        precision={0.5}
                        readOnly
                        size="small"
                        aria-label={`Average rating ${displayAvg} out of 5`}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {displayAvg} ({count})
                    </Typography>
                </>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No reviews yet
                </Typography>
            )}
        </Stack>
    );
}

function WriteReviewForm({saving, onSave}) {
    const [score, setScore] = useState(0);
    const [comment, setComment] = useState('');
    const [localError, setLocalError] = useState(null);

    const handleSave = async () => {
        setLocalError(null);
        if (!score || score < 1 || score > 5) {
            setLocalError('Please select a rating from 1 to 5 stars.');
            return;
        }
        try {
            await onSave({score, comment: comment.trim()});
            setScore(0);
            setComment('');
        } catch (err) {
            setLocalError(err?.message || 'Failed to save evaluation');
        }
    };

    return (
        <Stack spacing={1.5} data-testid="iir-my-review-form">
            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                Write a review
            </Typography>
            <Rating
                name="iir-my-score"
                value={score}
                precision={0.5}
                onChange={(_, next) => setScore(next || 0)}
                size="medium"
            />
            <TextField
                label="Comment"
                multiline
                minRows={2}
                fullWidth
                size="small"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your assessment of this IIR"
            />
            {localError && (
                <Alert severity="error" onClose={() => setLocalError(null)}>
                    {localError}
                </Alert>
            )}
            <Box>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Saving…' : 'Submit review'}
                </Button>
            </Box>
        </Stack>
    );
}

function ReviewCard({review, actions = null}) {
    return (
        <Stack spacing={0.75}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <ReviewerHeader review={review}/>
                {actions}
            </Stack>
            <Rating value={Number(review.score) || 0} precision={0.5} readOnly size="small"/>
            {review.comment ? (
                <Typography variant="body2" color="text.primary" sx={{whiteSpace: 'pre-wrap'}}>
                    {review.comment}
                </Typography>
            ) : null}
        </Stack>
    );
}

function EditableMyReview({review, saving, onSave, onDelete}) {
    const [editing, setEditing] = useState(false);
    const [score, setScore] = useState(review?.score ?? 0);
    const [comment, setComment] = useState(review?.comment ?? '');
    const [localError, setLocalError] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        setScore(review?.score ?? 0);
        setComment(review?.comment ?? '');
    }, [review?.score, review?.comment]);

    const handleCancel = () => {
        setLocalError(null);
        setScore(review?.score ?? 0);
        setComment(review?.comment ?? '');
        setEditing(false);
    };

    const handleSave = async () => {
        setLocalError(null);
        if (!score || score < 1 || score > 5) {
            setLocalError('Please select a rating from 1 to 5 stars.');
            return;
        }
        try {
            await onSave({score, comment: comment.trim()});
            setEditing(false);
        } catch (err) {
            setLocalError(err?.message || 'Failed to save evaluation');
        }
    };

    const handleDelete = async () => {
        setLocalError(null);
        setDeleting(true);
        try {
            await onDelete();
        } catch (err) {
            setLocalError(err?.message || 'Failed to delete evaluation');
        } finally {
            setDeleting(false);
        }
    };

    const actionButtons = (
        <Stack direction="row" spacing={0.25} sx={{mt: -0.5, flexShrink: 0}}>
            <IconButton
                size="small"
                aria-label="Edit your review"
                onClick={() => setEditing(true)}
                disabled={saving || deleting}
            >
                <EditOutlinedIcon fontSize="small"/>
            </IconButton>
            <IconButton
                size="small"
                aria-label="Delete your review"
                onClick={handleDelete}
                disabled={saving || deleting}
            >
                <DeleteOutlineIcon fontSize="small"/>
            </IconButton>
        </Stack>
    );

    if (!editing) {
        return (
            <Stack spacing={1} data-testid="iir-my-review-view">
                <ReviewCard review={review} actions={actionButtons}/>
                {localError && (
                    <Alert severity="error" onClose={() => setLocalError(null)}>
                        {localError}
                    </Alert>
                )}
            </Stack>
        );
    }

    return (
        <Stack spacing={1.5} data-testid="iir-my-review-edit">
            <ReviewerHeader review={review}/>
            <Rating
                name="iir-my-score-edit"
                value={score}
                precision={0.5}
                onChange={(_, next) => setScore(next || 0)}
                size="small"
            />
            <TextField
                multiline
                minRows={2}
                fullWidth
                size="small"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your assessment of this IIR"
            />
            {localError && (
                <Alert severity="error" onClose={() => setLocalError(null)}>
                    {localError}
                </Alert>
            )}
            <Stack direction="row" spacing={1}>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    onClick={handleSave}
                    disabled={saving || deleting}
                >
                    {saving ? 'Saving…' : 'Update review'}
                </Button>
                <Button
                    variant="text"
                    size="small"
                    onClick={handleCancel}
                    disabled={saving || deleting}
                >
                    Cancel
                </Button>
            </Stack>
        </Stack>
    );
}

function ReviewsList({evaluations}) {
    if (!evaluations?.length) return null;

    return (
        <Stack spacing={1.5} data-testid="iir-reviews-list">
            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                All reviews
            </Typography>
            {evaluations.map((ev, idx) => {
                const key = ev.id ?? `${ev.reviewerUserId || 'anon'}-${idx}`;
                return (
                    <Stack key={key} spacing={0.75}>
                        <ReviewCard review={ev}/>
                        {idx < evaluations.length - 1 && <Divider sx={{pt: 1}}/>}
                    </Stack>
                );
            })}
        </Stack>
    );
}

/**
 * Accordion block with aggregate + optional my-review form/card + other reviews for approved IIRs.
 * Renders nothing and skips fetch when product is not an approved IIR.
 */
export default function IirEvaluationSection({
    reportId,
    productType,
    status,
    enabled = true,
}) {
    const eligible = Boolean(reportId) && isIirEvaluationEligible({productType, status, enabled});
    const {
        summary,
        evaluations,
        mine,
        canEvaluate,
        loading,
        saving,
        error,
        saveMine,
        deleteMine,
    } = useIirEvaluations(reportId, {enabled: eligible});

    const otherReviews = useMemo(() => {
        if (!evaluations?.length) return [];
        if (!mine?.reviewerUserId) return evaluations;
        return evaluations.filter((ev) => ev.reviewerUserId !== mine.reviewerUserId);
    }, [evaluations, mine?.reviewerUserId]);

    if (!eligible) return null;

    const count = summary?.count ?? 0;

    return (
        <ThemeProvider theme={theme}>
            <Box
                data-testid="iir-evaluation-section"
                data-report-scroll-skip
                sx={{width: '100%'}}
            >
                <Accordion
                    defaultExpanded={false}
                    disableGutters
                    elevation={0}
                    sx={{
                        width: '100%',
                        border: 'none',
                        borderRadius: 0,
                        boxShadow: 'none',
                        bgcolor: 'transparent',
                        '&:before': {display: 'none'},
                        '&.Mui-expanded': {margin: 0},
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon/>}
                        sx={{
                            px: 0,
                            minHeight: 48,
                            '&.Mui-expanded': {minHeight: 48},
                            '& .MuiAccordionSummary-content': {my: 0.5},
                        }}
                    >
                        <AccordionTitle averageScore={summary?.averageScore} count={count}/>
                    </AccordionSummary>
                    <AccordionDetails sx={{px: 0, pt: 0, pb: 1}}>
                    <Stack spacing={2}>
                        {loading && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={18}/>
                                <Typography variant="body2" color="text.secondary">
                                    Loading evaluations…
                                </Typography>
                            </Stack>
                        )}

                        {error && !loading && (
                            <Alert severity="warning">
                                {error.message || 'Could not load evaluations'}
                            </Alert>
                        )}

                        {!loading && canEvaluate && mine && (
                            <EditableMyReview
                                review={mine}
                                saving={saving}
                                onSave={saveMine}
                                onDelete={deleteMine}
                            />
                        )}

                        {!loading && canEvaluate && !mine && (
                            <WriteReviewForm saving={saving} onSave={saveMine}/>
                        )}

                        {!loading && otherReviews.length > 0 && (
                            <>
                                {mine && <Divider/>}
                                <ReviewsList evaluations={otherReviews}/>
                            </>
                        )}
                    </Stack>
                    </AccordionDetails>
                </Accordion>
            </Box>
        </ThemeProvider>
    );
}
